import React, { useState } from 'react';
import {
  Box, Button, Card, Typography, IconButton, Tooltip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalText from '../common/LocalText';
import { useLocalization } from '../../context/LocalizationContext';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';
import WithdrawButton from '../approval/WithdrawButton';
import ContentPreviewDrawer from '../approval/ContentPreviewDrawer';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const getInitials = (name = '') =>
  (name || '?').split(' ').slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase() || '?';

const viewBtnSx = {
  borderColor: '#3b82f6',
  color: '#1d4ed8',
  bgcolor: '#eff6ff',
  borderRadius: '8px',
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const AdvertiserBrandCard = ({ advertiser, onSelect, onEdit, onDelete, onSuccess, onView, onEditRejected, preview = false, showActions = true }) => {
  const { locale } = useLocalization();
  const [viewProposalOpen, setViewProposalOpen] = useState(false);

  const name =
    advertiser.localizedName?.values?.find((v) => v.localeId === locale)?.value ||
    advertiser.localizedName?.values?.[0]?.value ||
    '';
  const approvalStatus     = advertiser.approvalStatus;
  const approvalId         = advertiser.approvalId;
  const approvalActionType = advertiser.approvalActionType;
  const contentStatus      = advertiser.contentStatus;
  const isSnapshot         = advertiser._viewType === 'snapshot';
  const isLocked           = approvalStatus === 'pending' || approvalStatus === 'rejected';
  const canDirectEdit      = contentStatus === 'draft' && !isLocked;

  const viewApprovalRecord = approvalId ? {
    approvalId:       approvalId,
    actionType:       approvalActionType,
    contentType:      CONTENT_TYPES.ADVERTISER,
    sectionLabel:     'Advertiser',
    contentName:      name,
    submittedByName: '',
    submittedAt:      null,
    status:            approvalStatus,
  } : null;

  const editTooltip = isLocked
    ? approvalStatus === 'pending'
      ? 'A pending proposal exists — View it or Withdraw first'
      : 'A rejected proposal exists — View it or Dismiss first'
    : canDirectEdit ? 'Edit Advertiser' : 'Propose edit (requires approval)';

  const deleteTooltip = isLocked
    ? approvalStatus === 'pending'
      ? 'A pending proposal exists — Withdraw it first'
      : 'A rejected proposal exists — Dismiss it first'
    : canDirectEdit ? 'Delete Advertiser' : 'Propose deletion (requires approval)';

  return (
    <Card
      sx={{
        bgcolor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        border: '2px solid transparent',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        ...(!preview && {
          '&:hover': {
            boxShadow: '0 8px 24px rgba(10,50,109,0.15)',
            borderColor: '#0a326d',
            transform: 'translateY(-2px)',
            '& .manage-link': { color: '#0a326d' },
          },
        }),
      }}
    >
      {/* Snapshot version banner */}
      {isSnapshot && (
        <Box sx={{
          bgcolor: approvalStatus === 'pending' ? '#d97706' : '#dc2626',
          color: 'white', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 600, textAlign: 'center',
        }}>
          {approvalStatus === 'pending' ? 'Proposed — Pending Review' : 'Proposed — Rejected'}
        </Box>
      )}

      {/* Card body */}
      <Box
        onClick={preview || isSnapshot ? undefined : () => onSelect(advertiser)}
        sx={{ p: 2.5, pb: 1.5, cursor: preview || isSnapshot ? 'default' : 'pointer' }}
      >
        <Box display="flex" alignItems="flex-start" gap={2} mb={1.5}>
          <Box
            sx={{
              width: 52, height: 52, borderRadius: '14px', bgcolor: '#0a326d',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, boxShadow: '0 4px 12px rgba(10,50,109,0.3)',
            }}
          >
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 16, letterSpacing: 0.5 }}>
              {getInitials(name)}
            </Typography>
          </Box>

          <Box flex={1} minWidth={0} pt={0.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e3a5f', lineHeight: 1.3, mb: 0.3 }} noWrap>
              {name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
              {advertiser.industry}
            </Typography>
          </Box>
        </Box>

        {/* Status chip + Edit / Delete */}
        <Box display="flex" alignItems="center" justifyContent="space-between" onClick={(e) => e.stopPropagation()}>
          <Box display="flex" alignItems="center" gap={1}>
            {/* Active/Inactive badge — hidden for now
            {!isSnapshot && (
              <Chip
                label={advertiser.isActive ? <LocalText>Active</LocalText> : <LocalText>Inactive</LocalText>}
                size="small"
                sx={{
                  height: 22, fontSize: 10, fontWeight: 700,
                  bgcolor: advertiser.isActive ? '#e8f5e9' : '#f5f5f5',
                  color: advertiser.isActive ? '#2e7d32' : '#757575',
                  border: `1px solid ${advertiser.isActive ? '#a5d6a7' : '#e0e0e0'}`,
                }}
              />
            )}
            */}
            {(isSnapshot ? approvalStatus : contentStatus) && (
              <ApprovalStatusBadge
                status={isSnapshot ? approvalStatus : contentStatus}
                actionType={approvalActionType}
              />
            )}
          </Box>
          {!preview && !isSnapshot && (
            <Box display="flex" gap={0.5}>
              {isLocked ? (
                <Tooltip title="View live content (read-only)">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.currentTarget.blur(); onView?.(advertiser); }}
                    sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                  >
                    <VisibilityIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              ) : showActions ? (
                <Tooltip title={editTooltip}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.currentTarget.blur(); onEdit(advertiser, !canDirectEdit); }}
                    sx={{ '&:hover': { bgcolor: '#EAF0F6' } }}
                  >
                    <EditIcon sx={{ fontSize: 15, color: '#0a326d' }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="View advertiser">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.currentTarget.blur(); onView?.(advertiser); }}
                    sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                  >
                    <VisibilityIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {showActions && (
                <Tooltip title={deleteTooltip}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={isLocked}
                      onClick={(e) => { e.currentTarget.blur(); onDelete(advertiser, !canDirectEdit); }}
                      sx={{ '&:hover': { bgcolor: '#fff0f0' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 15, color: '#d32f2f' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Approval action row */}
        {!preview && (isSnapshot || (!isSnapshot && canDirectEdit && contentStatus === 'draft')) && (
          <Box display="flex" flexWrap="wrap" gap={1} mt={1} onClick={(e) => e.stopPropagation()}>
            {/* True draft only (no ca record): Submit for Approval */}
            {!isSnapshot && canDirectEdit && contentStatus === 'draft' && showActions && (
              <SubmitForApprovalButton contentType={CONTENT_TYPES.ADVERTISER} contentId={advertiser.id} contentName={name} onSubmitted={() => onSuccess?.('pending')} />
            )}

            {/* Snapshot: View button */}
            {isSnapshot && viewApprovalRecord && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                onClick={() => setViewProposalOpen(true)}
                sx={viewBtnSx}
              >
                View
              </Button>
            )}
            {/* Snapshot pending: Withdraw */}
            {isSnapshot && approvalStatus === 'pending' && approvalId && showActions && (
              <WithdrawButton approvalId={approvalId} onWithdrawn={() => onSuccess?.(approvalActionType === 'create' ? 'draft' : 'live')} />
            )}
            {/* Snapshot rejected: Dismiss only */}
            {isSnapshot && approvalStatus === 'rejected' && approvalId && showActions && (
              <WithdrawButton approvalId={approvalId} onWithdrawn={() => onSuccess?.(approvalActionType === 'create' ? 'draft' : 'live')} variant="dismiss" />
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      {!preview && !isSnapshot && (
        <Box
          onClick={() => onSelect(advertiser)}
          sx={{ borderTop: '1px solid #EAF0F6', px: 2.5, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer' }}
        >
          <Box className="manage-link" display="flex" alignItems="center" gap={0.5} sx={{ color: '#5383b3', transition: 'color 0.15s ease' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
              <LocalText>Manage</LocalText>
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 13 }} />
          </Box>
        </Box>
      )}

      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={onEditRejected ? (snap) => { setViewProposalOpen(false); onEditRejected(snap, advertiser._liveId ?? advertiser.id); } : undefined}
        />
      )}
    </Card>
  );
};

export default AdvertiserBrandCard;
